import React, { useRef, useEffect, useMemo } from 'react';
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { insm } from '@atlaskit/insm';
import Spinner from '@atlaskit/spinner/spinner';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box } from '@atlaskit/primitives';
import { updatePageLoadInteractionName } from '@atlaskit/react-ufo/interaction-metrics';

import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag.tsx';
import { useFeatureCodesInURL } from '@confluence/feature-codes/entry-points/useFeatureCodesInURL.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import type { LoadingPriority } from '@confluence/loadable';
import { useGetCommentLinkType } from '@confluence/comments-util/entry-points/useGetCommentLinkType.ts';
import {
	CREATE_PAGE_EXPERIENCE,
	VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { useExperienceState } from '@confluence/experience-tracker/entry-points/useExperienceState.ts';
import { FullPageEditorLoader } from '@confluence/full-page-editor/entry-points/FullPageEditorLoader.ts';
import { TreeRootForServerPlaceholderWrapper } from '@confluence/full-page-editor/entry-points/TreeRootForServerPlaceholderWrapper.ts';
import type { NativeCollabInjectedProps } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import { NativeCollabLoader } from '@confluence/native-collab/entry-points/NativeCollabLoader.ts';
import { ContentViewedEvent } from '@confluence/content-body/entry-points/ContentViewedEvent.ts';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { LivePageInviteNudge } from '@confluence/onboarding-editor/entry-points/components/LivePageInviteNudge.ts';
import { useLivePageOnboardingNudgeEligible } from '@confluence/onboarding-editor/entry-points/hooks/useLivePageOnboardingNudgeEligible.ts';
import { fg } from '@confluence/feature-gating';
import { NewCommentsProvider } from '@confluence/comment-context/entry-points/NewCommentsContext.ts';
import { PageCommentsCountProvider } from '@confluence/comment-context/entry-points/PageCommentsCountContext.ts';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/onboarding-nth-user-setup-modal/entry-points/OnboardingNthUserContentTypeSpotlight.tsx';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader.ts';
import { StartLivePageLoad } from '@confluence/live-pages-perf/entry-points/live-pages-perf.config.ts';
import { FullCycleRenderWrapper } from '@confluence/ssr-utilities/entry-points/FullCycleRender.ts';
import { RemoteLinkFlag } from '@confluence/experiment-jira-smart-links/entry-points/RemoteLinkFlagEntryPoint.ts';

import { useEditorDataLoader } from './useEditorDataLoader.tsx';
import { triggerViewedAnalytics } from './LivePageViewedAnalytics.ts';
import { usePermissionCheck } from './usePermissionCheck.tsx';
import { LivePageSessionExperienceStart } from './LivePageSessionExperienceStart.tsx';
import { LivePageShortcuts } from './LivePageShortcuts.tsx';

export type LivePageComponentProps = {
	contentId: string;
	spaceKey: string | undefined;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	customPerfData?: Record<string, string | boolean | object>;
	loadingPriority?: LoadingPriority | null;

	// When LivePageComponent goes from not fast loaded -> fast loaded or vice-versa, ViewPageCommon remounts LivePageComponent (via renderLivePageEarly).
	// We need to inform LivePageContent if we're likely to remount, as that could cause NCS issues.
	isFastLoaded?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
	hasPageComments: boolean;
	hasInlineComments: boolean;
};
type LivePageContentProps = {
	canEdit: boolean;
	isSeamlessTransition: boolean;
	refetch: () => void;
	onNativeCollabLoad?: (contentId: string, viewMode: boolean | undefined) => void;
	onDraftSyncSuccess?: () => void; // Used to hook into the editor draft sync mutation
} & LivePageComponentProps;

const spinnerContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	height: '100vh',
});

// Handle aborting experiences
/* Why we're aborting each experience:
 * VIEW_PAGE_SESSION_EXPERIENCE: Aborted due to not wanting live pages to affect classic view-page-specific experience. Live pages have their own live-page-session experience.
 * VIEW_PAGE_EDIT_BUTTON_EXPERIENCE: Aborted due to editable live page starting but never finishing view-page/edit, because in ViewPageCommon, stickyHeader briefly mounts while loading `globalLiveState.isLive` and `editPermission`, which we unmount after we determine doc is an editable live page
 */
const useAbortNonLiveExperiences = () => {
	const experienceTracker = React.useContext(ExperienceTrackerContext);
	const { hasStopped: hasViewPageSessionExperienceStopped } =
		useExperienceState(VIEW_PAGE_SESSION_EXPERIENCE) || {};
	const { hasStopped: hasViewPageEditButtonExperienceStopped } =
		useExperienceState(VIEW_PAGE_EDIT_BUTTON_EXPERIENCE) || {};

	const nonCoreExperiencesToAbort = [
		{ name: VIEW_PAGE_SESSION_EXPERIENCE, hasStopped: hasViewPageSessionExperienceStopped },
		{
			name: VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
			hasStopped: hasViewPageEditButtonExperienceStopped,
		},
	];

	nonCoreExperiencesToAbort.forEach((abortedExperience) => {
		if (!abortedExperience.hasStopped) {
			experienceTracker.abort({
				name: abortedExperience.name,
				reason: 'Live Page Editor was opened',
			});
		}
	});
};

export const LivePageContent = ({
	contentId,
	spaceKey,
	canEdit,
	refetch,
	ClassicEditorContextProviderLoader,
	onNativeCollabLoad,
	customPerfData,
	onDraftSyncSuccess,
	loadingPriority,
	isSeamlessTransition,
	isFastLoaded,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasPageComments,
	hasInlineComments,
}: LivePageContentProps) => {
	const { loomExternalOpenRoute } = useEmbeddedViewPageFeatures();

	useAbortNonLiveExperiences();

	const lastNativeCollabKeyRef = useRef(contentId);
	// NativeCollabComponent MUST remount if we're not a seamless transition, and it MUST NOT remount if we are a seamless transition.
	// Changing the key triggers a remount.
	// This means the key needs to stay the same if we go from non-seamless transition -> seamless transition
	// This fixes a bug where NativeCollabComponent unmounting on transition from non-seamless -> seamless would
	//   bug out the collab provider editor plugin, preventing NCS from connecting
	const nativeCollabKey = useMemo(() => {
		if (!isSeamlessTransition) {
			lastNativeCollabKeyRef.current = contentId;
			return contentId;
		}
		return lastNativeCollabKeyRef.current;
	}, [contentId, isSeamlessTransition]);

	const LivePageWrapper = ClassicEditorContextProviderLoader ?? React.Fragment;

	// If not seamless transition, ensure key is passed to NativeCollabLoader so it can clean up NCS provider on unmount when contentId changes
	return (
		<LivePageWrapper>
			{!isSeamlessTransition && <StartLivePageLoad contentId={contentId} viewMode={!canEdit} />}
			<ContentViewedEvent contentId={contentId} />
			<ExperienceSuccess name={CREATE_PAGE_EXPERIENCE} attributes={{ isLivePage: true }} />
			<LivePageSessionExperienceStart />
			<NativeCollabLoader
				contentId={contentId}
				spaceKey={spaceKey}
				contentType="page"
				key={nativeCollabKey}
				onPermissionChange={refetch}
				canEdit={canEdit}
			>
				{(propsFromNativeCollab: NativeCollabInjectedProps) => {
					const { viewMode } = propsFromNativeCollab;
					const livePageCustomPerfData = {
						...customPerfData,
						viewMode,
					};
					// TODO MODES-3871 Remove this triggerViewedAnalytics side effect when we cleanup LivePageComponent.
					// Trigger live page view analytics event once when the user views a live page.
					onNativeCollabLoad?.(contentId, viewMode);

					return (
						// This TreeRootForServerPlaceholderWrapper placed here to ensure the proper lookup of the placeholders for
						// SSRed loadables. Due to different component trees between server (LivePageComponent) and client (LivePageComponentServer)
						// the placeholder ids hierarchy is different. This wrapper terminates the lookup for placeholder ids on the common parent,
						// so it matches on both server and client.
						expValEquals('platform_editor_editor_ssr_streaming', 'isEnabled', true) ? (
							<SuspenseWithDataFetcher dataFetcherName="content-main">
								<TreeRootForServerPlaceholderWrapper>
									<FullPageEditorLoader
										{...propsFromNativeCollab}
										extensionContextProvider={{
											ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
										}}
										hasEditPermission={canEdit}
										customPerfData={livePageCustomPerfData}
										onDraftSyncSuccess={onDraftSyncSuccess}
										loadingPriority={loadingPriority}
										isFastLoadedLiveDoc={isFastLoaded}
										hasByLineContributors={hasByLineContributors}
										hasByLineExtensions={hasByLineExtensions}
										hasReactions={hasReactions}
										hasPageComments={hasPageComments}
										hasInlineComments={hasInlineComments}
										loomExternalOpenRoute={loomExternalOpenRoute}
									/>
									<LivePageShortcuts canEdit={canEdit} />
								</TreeRootForServerPlaceholderWrapper>
							</SuspenseWithDataFetcher>
						) : (
							<FullCycleRenderWrapper>
								<TreeRootForServerPlaceholderWrapper>
									<FullPageEditorLoader
										{...propsFromNativeCollab}
										extensionContextProvider={{
											ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
										}}
										hasEditPermission={canEdit}
										customPerfData={livePageCustomPerfData}
										onDraftSyncSuccess={onDraftSyncSuccess}
										loadingPriority={loadingPriority}
										isFastLoadedLiveDoc={isFastLoaded}
										hasByLineContributors={hasByLineContributors}
										hasByLineExtensions={hasByLineExtensions}
										hasReactions={hasReactions}
										hasPageComments={hasPageComments}
										hasInlineComments={hasInlineComments}
										loomExternalOpenRoute={loomExternalOpenRoute}
									/>
									<LivePageShortcuts canEdit={canEdit} />
								</TreeRootForServerPlaceholderWrapper>
							</FullCycleRenderWrapper>
						)
					);
				}}
			</NativeCollabLoader>
		</LivePageWrapper>
	);
};

export const LivePageComponent = ({
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	customPerfData,
	loadingPriority,
	isFastLoaded,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
	hasPageComments,
	hasInlineComments,
}: LivePageComponentProps) => {
	const {
		hasData: hasPermissionCheckData,
		canEdit,
		loading: isLoadingPermissionCheck,
		contentQueryError,
		refetch,
		networkStatus,
	} = usePermissionCheck(contentId);
	const prevCanEditRef = useRef<boolean | undefined>(undefined);
	const hasSentViewedAnalytics = useRef<boolean>(false);

	const { setMode } = useLivePageStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isPreloading } = useEditorDataLoader({
		isLoadingPermissionCheck,
		contentId,
		spaceKey,
		canEdit,
	});
	const featureCodes = useFeatureCodesInURL();
	const commentLink = useGetCommentLinkType();

	const { showLivePageInviteFlag } = useLivePageOnboardingNudgeEligible({ contentId });

	const transitionPreloaderCurrentCallbackIDs =
		getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
	const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
		? transitionPreloaderCurrentCallbackIDs.includes('live_doc_preloading_transition_callback_id')
		: false;

	useEffect(() => {
		if (!expValEquals('confluence_dedupe_page_viewed_event', 'isEnabled', true)) {
			hasSentViewedAnalytics.current = false;
		}

		return () => {
			// Reset the mode and analytics flag when contentId changes or component unmounts
			void setMode(undefined);
			prevCanEditRef.current = undefined;
			if (expValEquals('confluence_dedupe_page_viewed_event', 'isEnabled', true)) {
				hasSentViewedAnalytics.current = false;
			}
		};
	}, [contentId, setMode]);

	useEffect(() => {
		// Permissions on a page can change at anytime. Should update the mode if the new permissions are different from the previous ones.
		const hasPermissionsChanged = canEdit !== prevCanEditRef.current;
		if (hasPermissionsChanged) {
			void setMode(canEdit ? 'edit' : 'view');
		}
		prevCanEditRef.current = canEdit;
	}, [canEdit, contentId, setMode]);

	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const liveEditUFOName = isEmbeddedConfluence ? 'embedded-live-edit' : 'live-edit';

	const { overrideUFORouteName } = useRouteActions();
	useEffect(() => {
		overrideUFORouteName(liveEditUFOName);
		// overrideUFORouteName only updates the trace-based UFO system. For embedded
		// pages, we also need to update the legacy interaction-metrics system, which
		// was set to 'embedded-view-page' by EmbeddedViewPage.
		if (isEmbeddedConfluence) {
			updatePageLoadInteractionName(liveEditUFOName);
		}
		if (expValEquals('cc_editor_interactivity_monitoring', 'liveDocs', true)) {
			insm.overrideExperienceKey(liveEditUFOName);
		}
		// Live doc transitions trigger a UFO route change but usually don't trigger a LivePageComponent remount, so we need to re-run on contentId change
	}, [overrideUFORouteName, contentId, liveEditUFOName, isEmbeddedConfluence]);

	const handleNativeCollabLoad = (contentId: string, viewMode: boolean | undefined) => {
		if (hasSentViewedAnalytics.current === false) {
			void triggerViewedAnalytics({
				contentId,
				editPermission: canEdit,
				createAnalyticsEvent,
				viewMode,
				...(featureCodes && featureCodes.from ? { from: featureCodes.from } : {}),
				...(commentLink && { commentLink }),
			});
			hasSentViewedAnalytics.current = true;
		}
	};

	if (contentQueryError) {
		return <ErrorDisplay error={contentQueryError} />;
	}

	const skipLoadingScreenWhenSSRed = window.__SSR_RENDERED__;
	if (
		((isPreloading && !skipLoadingScreenWhenSSRed) ||
			// don't unmount live pages editor on permission refetch
			(expValEquals('platform_editor_editor_ssr_streaming', 'isEnabled', true)
				? !hasPermissionCheckData
				: isLoadingPermissionCheck && networkStatus !== NetworkStatus.refetch)) &&
		!isSeamlessTransition
	) {
		return (
			<Box xcss={spinnerContainerStyles}>
				<Spinner interactionName="live-pages-loading-spinner" size="medium" />
			</Box>
		);
	}

	return (
		<NewCommentsProvider>
			<PageCommentsCountProvider contentId={contentId}>
				{expVal('confluence_modernized_move_dialog', 'isEnabled', false) && <MovePageSuccessFlag />}
				{fg('cc_fd_jira_issue_live_doc_prompt_switch') && <RemoteLinkFlag contentId={contentId} />}
				<LivePageContent
					contentId={contentId}
					spaceKey={spaceKey}
					ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
					canEdit={canEdit}
					refetch={refetch}
					onNativeCollabLoad={handleNativeCollabLoad}
					onDraftSyncSuccess={showLivePageInviteFlag}
					customPerfData={customPerfData}
					loadingPriority={loadingPriority}
					isSeamlessTransition={isSeamlessTransition}
					isFastLoaded={isFastLoaded}
					hasByLineContributors={hasByLineContributors}
					hasByLineExtensions={hasByLineExtensions}
					hasReactions={hasReactions}
					hasPageComments={hasPageComments}
					hasInlineComments={hasInlineComments}
				/>
				<OnboardingNthUserContentTypeSpotlight
					contentType={ContentType.LIVE_DOC}
					contentId={contentId}
				/>
				<LivePageInviteNudge />
			</PageCommentsCountProvider>
		</NewCommentsProvider>
	);
};
